/**
 * The identity block at the top of the user detail page.
 *
 * Read-only on purpose: everything here is also editable somewhere in the tabs below, and a header
 * that both states the facts and changes them is how you end up with two controls for the same
 * flag disagreeing on a phone, where only one of them is on screen.
 */

import { Avatar, Box, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material'
import { WarningAmber as WarningAmberIcon } from '@mui/icons-material'
import type { AdminUserDto } from '@api/types/admin'
import { formatDate } from './userFormatting'

export interface UserHeaderProps {
  user: AdminUserDto
}

function roleColor(role: string): 'error' | 'warning' | 'default' {
  if (role === 'Admin') return 'error'
  if (role === 'Moderator') return 'warning'
  return 'default'
}

export function UserHeader({ user }: UserHeaderProps) {
  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          // On a phone the avatar sits above the name instead of stealing a third of the width.
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: { xs: 1.5, sm: 3 },
        }}
      >
        <Avatar src={user.avatar || user.profilePictureUrl} sx={{ width: 72, height: 72 }}>
          {user.displayName?.charAt(0).toUpperCase() || 'U'}
        </Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="h6" sx={{ lineHeight: 1.3 }}>
            {user.displayName}
          </Typography>
          {user.userName && (
            <Typography variant="body2" color="text.secondary">
              @{user.userName}
            </Typography>
          )}
          {/* Long addresses have to be allowed to wrap, or they push the page sideways at 390px. */}
          <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
            {user.email}
          </Typography>

          <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
            <Chip
              label={user.isActive ? 'Active' : 'Inactive'}
              color={user.isActive ? 'success' : 'default'}
              size="small"
            />
            {(user.roles || []).map((role) => (
              <Chip key={role} label={role} size="small" color={roleColor(role)} variant="outlined" />
            ))}
            {/* Only when there is something to say. A "0 mocked" badge on every account would read
                as a clean bill of health, and it is not one: zero is also what an iPhone and a
                browser report, because neither can tell. */}
            {!!user.mockedLocationReports && (
              <Tooltip
                title={
                  `${user.mockedLocationReports} location report` +
                  `${user.mockedLocationReports === 1 ? '' : 's'} flagged by the device as mocked` +
                  (user.lastMockedLocationAt
                    ? `, most recently ${new Date(user.lastMockedLocationAt).toLocaleString()}`
                    : '') +
                  '. Evidence, not proof — it does not catch a rooted device, and it is reported by the app itself.'
                }
              >
                <Chip
                  icon={<WarningAmberIcon />}
                  label={`Mocked ×${user.mockedLocationReports}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Stack>
        </Box>

        <Box sx={{ flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Joined {formatDate(user.createdAt)}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            Last login {formatDate(user.lastLoginAt)}
          </Typography>
        </Box>
      </Box>
    </Paper>
  )
}
